/**
 * Shared Query Period filter helpers (Date Range vs From/To).
 * Used by measurement pages, charts, and follow-up list/query screens.
 */

import {
  compareDateOnly,
  getTodayDateOnlyLocal,
  normalizeDateOnly,
} from './dateOnlyUtils';

export const DEFAULT_MEASUREMENT_QUERY = Object.freeze({
  queryType: 'dateRange',
  dateRange: 'Last 30 days',
  fromDate: '',
  toDate: '',
  sortBy: 'Weekly average',
});

export const DEFAULT_FOLLOW_UP_LIST_FILTERS = Object.freeze({
  dateRangeType: 'dateRange',
  dateRange: 'Last 30 days',
  fromDate: '',
  toDate: '',
  serviceTime: '',
  followUpStatus: '',
});

/** @deprecated Use DEFAULT_FOLLOW_UP_LIST_FILTERS */
export const DEFAULT_FOLLOW_UP_FILTERS = DEFAULT_FOLLOW_UP_LIST_FILTERS;

/** Normalize 'from' | 'fromTo' as custom-range mode. */
export function isCustomRangeType(type) {
  return type === 'from' || type === 'fromTo';
}

/** Max selectable date for measurement From/To filters (today, local). */
export function getMeasurementQueryMaxDate() {
  return getTodayDateOnlyLocal();
}

/** Clamp From date: no future dates. */
export function sanitizeMeasurementFromDate(value) {
  const ymd = normalizeDateOnly(value);
  if (!ymd) return '';
  const today = getTodayDateOnlyLocal();
  return ymd > today ? today : ymd;
}

/** Clamp To date: not before From, not after today. */
export function sanitizeMeasurementToDate(value, fromDate) {
  const ymd = normalizeDateOnly(value);
  if (!ymd) return '';
  const today = getTodayDateOnlyLocal();
  const from = normalizeDateOnly(fromDate);
  let result = ymd > today ? today : ymd;
  if (from && result < from) result = from;
  return result;
}

/** Apply From change and adjust To when it falls outside the valid range. */
export function applyMeasurementFromDateChange(value, currentToDate) {
  const fromDate = sanitizeMeasurementFromDate(value);
  const toDate = currentToDate
    ? sanitizeMeasurementToDate(currentToDate, fromDate)
    : '';
  return { fromDate, toDate };
}

/** Query disabled when From/To missing, in the future, or To is before From. */
export function isFromToIncomplete(type, fromDate, toDate) {
  if (!isCustomRangeType(type)) return false;
  const from = normalizeDateOnly(fromDate);
  const to = normalizeDateOnly(toDate);
  if (!from || !to) return true;
  const today = getTodayDateOnlyLocal();
  return from > today || to > today || compareDateOnly(to, from) < 0;
}

/**
 * Switching to Date Range clears From/To so stale custom dates are not kept.
 * @returns {{ queryType?: string, dateRangeType?: string, fromDate: string, toDate: string }}
 */
export function patchOnQueryModeChange(nextType, options = {}) {
  const { typeKey = 'queryType' } = options;
  const patch = { [typeKey]: nextType };
  if (!isCustomRangeType(nextType)) {
    patch.fromDate = '';
    patch.toDate = '';
  }
  return patch;
}

export function isMeasurementQueryDirty({
  queryType,
  dateRange,
  fromDate,
  toDate,
  sortBy,
}, defaults = DEFAULT_MEASUREMENT_QUERY) {
  return (
    queryType !== defaults.queryType
    || dateRange !== defaults.dateRange
    || Boolean(fromDate)
    || Boolean(toDate)
    || (sortBy != null && defaults.sortBy != null && sortBy !== defaults.sortBy)
  );
}

export function followUpListFiltersEqual(a, b) {
  return (
    a.dateRangeType === b.dateRangeType
    && a.dateRange === b.dateRange
    && a.fromDate === b.fromDate
    && a.toDate === b.toDate
    && a.serviceTime === b.serviceTime
    && a.followUpStatus === b.followUpStatus
  );
}
